"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import {
  fetchGlsDeliveryPoints,
  geocodeShippingAddress,
  setGlsDeliveryPoint,
} from "@/lib/data/gls"
import {
  filterGlsDeliveryPoints,
  filterGlsPointsByPostalCode,
  filterGlsPointsByType,
  getCartPostalCode,
  getStoredGlsDeliveryPoint,
  sortGlsPointsByDistance,
  type GeoCoordinate,
  type GlsDeliveryPoint,
  type GlsDeliveryPointWithDistance,
  type GlsPointTypeFilter,
} from "@/lib/util/gls-shipping"
import { HttpTypes } from "@medusajs/types"

type UseGlsDeliveryPointsOptions = {
  cart: HttpTypes.StoreCart
  countryCode?: string
  onSaved?: () => void | Promise<void>
}

export function useGlsDeliveryPoints({
  cart,
  countryCode = "SI",
  onSaved,
}: UseGlsDeliveryPointsOptions) {
  const [query, setQuery] = useState("")
  const [typeFilter, setTypeFilter] = useState<GlsPointTypeFilter>(null)
  const [points, setPoints] = useState<GlsDeliveryPoint[]>([])
  const [homeCoordinate, setHomeCoordinate] = useState<GeoCoordinate | null>(
    null
  )
  const [addressLabel, setAddressLabel] = useState<string | null>(null)
  const [selectedPoint, setSelectedPoint] = useState<GlsDeliveryPoint | null>(
    getStoredGlsDeliveryPoint(cart)
  )
  const [focusedMatchcode, setFocusedMatchcode] = useState<string | null>(null)
  const [isBrowsing, setIsBrowsing] = useState(
    () => !getStoredGlsDeliveryPoint(cart)
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const postalCode = getCartPostalCode(cart)
  const isSearchingAll = query.trim().length > 0

  useEffect(() => {
    const stored = getStoredGlsDeliveryPoint(cart)
    setSelectedPoint(stored)
    if (stored) {
      setIsBrowsing(false)
    }
  }, [cart])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        const [response, geocoded] = await Promise.all([
          fetchGlsDeliveryPoints(countryCode),
          geocodeShippingAddress(cart.id),
        ])

        if (!cancelled) {
          setPoints(response.delivery_points || [])
          setHomeCoordinate(geocoded.coordinate)
          setAddressLabel(geocoded.addressLabel)
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Napaka pri nalaganju GLS prevzemnih mest."
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [cart.id, countryCode])

  const postalPoints = useMemo(
    () => filterGlsPointsByPostalCode(points, postalCode),
    [points, postalCode]
  )

  const filteredPoints = useMemo((): GlsDeliveryPointWithDistance[] => {
    const hasQuery = query.trim().length > 0
    const source = hasQuery ? points : postalPoints
    const textFiltered = filterGlsDeliveryPoints(source, query)
    const typeFiltered = filterGlsPointsByType(textFiltered, typeFilter)
    return sortGlsPointsByDistance(typeFiltered, homeCoordinate)
  }, [points, postalPoints, query, typeFilter, homeCoordinate])

  const mapPoints = useMemo((): GlsDeliveryPoint[] => {
    const hasQuery = query.trim().length > 0
    const textFiltered = hasQuery
      ? filterGlsDeliveryPoints(points, query)
      : points
    return filterGlsPointsByType(textFiltered, typeFilter)
  }, [points, query, typeFilter])

  const visiblePoints = useMemo(
    () => filteredPoints.slice(0, 40),
    [filteredPoints]
  )

  const mapAutoFitKey = `${query}|${typeFilter}|${postalCode}|${isSearchingAll}`

  const selectPoint = useCallback(
    async (point: GlsDeliveryPoint) => {
      setSaving(true)
      setError(null)

      try {
        await setGlsDeliveryPoint(cart.id, point)
        setSelectedPoint(point)
        setFocusedMatchcode(point.matchcode)
        setIsBrowsing(false)
        await onSaved?.()
      } catch (saveError) {
        setError(
          saveError instanceof Error
            ? saveError.message
            : "Napaka pri shranjevanju prevzemnega mesta."
        )
      } finally {
        setSaving(false)
      }
    },
    [cart.id, onSaved]
  )

  const focusPoint = useCallback((matchcode: string) => {
    setFocusedMatchcode(matchcode)
  }, [])

  const startBrowsing = useCallback(() => {
    setIsBrowsing(true)
    setFocusedMatchcode(selectedPoint?.matchcode ?? null)
  }, [selectedPoint?.matchcode])

  const contextMessage = useMemo(() => {
    if (isSearchingAll) {
      return "Iskanje po vseh GLS prevzemnih mestih v Sloveniji."
    }

    if (postalCode) {
      return addressLabel
        ? `Seznam prikazuje mesta v bližini ${addressLabel}. Na zemljevidu so vsa prevzemna mesta – premaknite ga za ogled drugih lokacij.`
        : `Seznam prikazuje mesta za poštno številko ${postalCode}. Na zemljevidu so vsa prevzemna mesta – premaknite ga za ogled drugih lokacij.`
    }

    return "Vnesite poštno številko v koraku naslova ali uporabite iskanje."
  }, [addressLabel, isSearchingAll, postalCode])

  return {
    query,
    setQuery,
    typeFilter,
    setTypeFilter,
    points,
    homeCoordinate,
    selectedPoint,
    focusedMatchcode,
    loading,
    saving,
    error,
    postalCode,
    isSearchingAll,
    mapPoints,
    mapAutoFitKey,
    visiblePoints,
    selectPoint,
    focusPoint,
    startBrowsing,
    isBrowsing,
    contextMessage,
  }
}
